import {Injectable} from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import {DipCustomerIdentity} from './dip-registration-customer-identification/models/dip-registration-customer-identity.model';
import {DipRegistrationCustomerIdentityResponse} from './dip-registration-customer-identification/models/dip-registration-customer-identity-response.model';
import {DipRegistrationCustomerTicketsResponse} from './dip-registration-customer-identification/models/dip-registration-customer-tickets-response.model';
import {DipRegistrationCustomerTokenResponse} from './core/models/dip-registration-customer-token-response.model';
import {TranslateService} from '@ngx-translate/core';
import {DipRegistrationLoggerService} from './core/services/logger/dip-registration-logger.service';
import {APP_VERSION, DipRegistrationContextService, SESSION_ID} from './core/services/dip-registration-context.service';
import {getApiUrl} from './core/services/utilities/dip-registration-utility';
import {DipRegistrationErrorsHandler} from './dip-registration-error/dip-registration-errors.handler';
import {DipRegistrationUAPState} from './core/models/uap/dip-registration-uap-state.model';
import {DipRegistrationContext} from './core/models/dip-registration-context.model';
import {DipRegistrationTMXInfo} from './core/models/dip-registration-tmx-info.model';
import {fromEvent, merge, of, Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';


@Injectable()
export class DipRegistrationService {
  sessionAliveSubscription: Subscription;

  constructor(private readonly http: HttpClient,
              private translateService: TranslateService,
              private dipRegistrationContextService: DipRegistrationContextService,
              private errorHandler: DipRegistrationErrorsHandler,
              private logger: DipRegistrationLoggerService
  ) {
  }


  async redirectToUAP() {
    const uapUrl = this.dipRegistrationContextService.getUapUrl();
    if (uapUrl) {
      try {
        window.location.replace(uapUrl);
      } catch (error) {
        await this.errorHandler.handleError(error);
      }
    }
  }

  private postForm(path, params, method) {
    method = method || 'post';

    const form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', key);
        hiddenField.setAttribute('value', params[key]);
        form.appendChild(hiddenField);
      }
    }
    document.body.appendChild(form);
    form.submit();
  }


  async callURP(customerTicketsResponse: DipRegistrationCustomerTicketsResponse): Promise<any> {
    const urpUrl = this.dipRegistrationContextService.getUrpUrl();
    this.logger.debug(`URP url is ${urpUrl}`);
    if (urpUrl) {
      try {
        await this.killSession();
        this.postForm(urpUrl, {
          ticketId: customerTicketsResponse.valetTicket,
          saml: customerTicketsResponse.samlToken,
          environmentId: this.dipRegistrationContextService.getConsumerAppId()
        }, 'post');
      } catch (error) {
        await this.errorHandler.handleError(error);
      }
    }
  }

  /**
   * Invalidate the session
   */
  async killSession(): Promise<void> {
    try {
      await this.http.delete<DipRegistrationContext>(`${getApiUrl()}/v1/context`, {observe: 'response'}).toPromise();
    } catch (error) {
      this.logger.error('Cannot invalidate the session', error);
    }
    return null;
  }

  /**
   * Reset the session and return a fresh context
   */
  async resetContext(): Promise<DipRegistrationContext> {
    const request = this.http.post<DipRegistrationContext>(`${getApiUrl()}/v1/context`, null, {observe: 'response'}).toPromise();
    return this.getContext(request);
  }

  /**
   * Reset the session and force app reload
   */
  async resetAndReload(reloadPath = '/overview') {
    await this.http.post<DipRegistrationContext>(`${getApiUrl()}/v1/context`, null, {observe: 'response'}).toPromise();
    this.logger.debug('AppContext reset');
    window.location.href = getApiUrl() + reloadPath + this.dipRegistrationContextService.buildQueryString();
  }

  /**
   * Get TMXID
   */
  async buildTmxInfo(): Promise<DipRegistrationTMXInfo> {
    return this.http.post<DipRegistrationTMXInfo>(`${getApiUrl()}/v1/tmx-info`, null).toPromise();
  }


  /**
   * Get TMXID
   */
  async getTmxInfo(): Promise<DipRegistrationTMXInfo> {
    return this.http.get<DipRegistrationTMXInfo>(`${getApiUrl()}/v1/tmx-info`).toPromise();
  }


  /**
   * init the session and return the new context
   */
  async initContext(uapState?: DipRegistrationUAPState): Promise<DipRegistrationContext> {
    const request = this.http.get<DipRegistrationContext>(`${getApiUrl()}/v1/context`, {observe: 'response'}).toPromise();
    return this.getContext(request, uapState);
  }

  private async getContext(request: Promise<HttpResponse<DipRegistrationContext>>, uapState?: DipRegistrationUAPState): Promise<DipRegistrationContext> {
    try {
      const contextResponse = await request;
      return this.dipRegistrationContextService.loadContext(contextResponse, uapState);
    } catch (error) {
      const sid = error?.headers?.get(SESSION_ID);
      const version = error?.headers?.get(APP_VERSION);
      this.errorHandler.handleMaintenancePageRedirection('CONTEXT_NOT_AVAILABLE', version, sid);
    }
    return null;
  }

  async checkSystemAvailability(apiUrl: string, uapState: DipRegistrationUAPState): Promise<any> {
    if (this.dipRegistrationContextService.isSkipSystemAvailability() || (uapState && uapState.skipSystemAvailability)) {
      return Promise.resolve();
    }
    try {
      await this.http.get<any>(`${apiUrl}/v1/system-availability`, {observe: 'response'}).toPromise();
    } catch (error) {
      const sid = error?.headers?.get(SESSION_ID);
      const version = error?.headers?.get(APP_VERSION);
      this.errorHandler.handleMaintenancePageRedirection('SYSTEM_NOT_AVAILABLE', version, sid);
    }
  }

  /**
   * This endpoint call do a composition between pingfed exchange and nrl to check if the user is registred
   * @param apiUrl the api url
   * @param authCode the authorization code
   */
  sendCustomerToken(apiUrl: string, authCode: string): Promise<DipRegistrationCustomerTokenResponse> {
    return this.http.post<DipRegistrationCustomerTokenResponse>(`${apiUrl}/v1/customer-token`, {
      authCode,
      redirectUri: apiUrl
    }).toPromise();
  }

  sendCustomerIdentity(customerIdentify: DipCustomerIdentity | {dipssGuid: string}): Promise<HttpResponse<DipRegistrationCustomerIdentityResponse>> {
    return this.http.post<DipRegistrationCustomerIdentityResponse>(`${getApiUrl()}/v1/customer-identity`, customerIdentify,  {observe: 'response'}).toPromise();
  }

  /**
   * get customer tickets for the telephone number
   * @param telephoneId phone number if empty we should setted to empty string
   */
  getCustomerTickets(telephoneNum: string): Promise<HttpResponse<DipRegistrationCustomerTicketsResponse>> {
    return this.http.post<DipRegistrationCustomerTicketsResponse>(`${getApiUrl()}/v1/customer-tickets`, {
      telephoneNum
    }, {observe: 'response'}).toPromise();
  }

  startKeepingSessionAlive() {
    let start = new Date().getTime();
    this.stopKeepingSessionAlive();
    this.sessionAliveSubscription = merge(
      of(null),
      fromEvent(window, 'mousedown'),
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'mousewheel'),
      fromEvent(window, 'touchstart'),
      fromEvent(window, 'touchmove'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'keydown')
    )
      .pipe(
        tap(async () => {
          const end = new Date().getTime();
          if ((end - start) > this.dipRegistrationContextService.getSessionWarningTimeout()) {
            this.logger.debug(`Arrived time the page is ${start} (milliseconds) and seems User is still active (current Activity time in the page is ${end} milliseconds), so we will keep session alive`);
            start = new Date().getTime();
            try {
              await this.initContext();
            } catch (err) {
              this.logger.error('Error getting the context in', err);
              this.errorHandler.handleMaintenancePageRedirection('APP_INIT_ERROR');
            }
          }
        })
      )
      .subscribe();
  }

  stopKeepingSessionAlive() {
    if (this.sessionAliveSubscription) {
      this.sessionAliveSubscription.unsubscribe();
    }
  }
}
